import "@/style/App.css";
import "@style/Admin.scss";
import "@style/TestSections.scss";
import "@style/sass/_testAnswerReplay.scss";
import { useEffect } from "react";
import SectionRenderer from "@/sections/SectionRenderer";
import TestAnswerPdfDetailsPage from "@/elements/TestAnswerPdfDetailsPage";
import type { SectionCurrentSection } from "@/sections/sectionRuntimeTypes";
import type { TestAnswerPdfDetails } from "@/utils/testAnswerPdfDetails";

export type TestAnswerReplaySection = {
    id: number;
    sectionId: number;
    name: string;
    formId: number;
    pages: number;
    order: number;
    contents: Record<string, string>;
};

export type TestAnswerReplayPayload = {
    testInstanceId: number;
    testId: number;
    languageAbbrev: string;
    testee: {
        firstName?: string;
        lastName?: string;
    };
    sections: TestAnswerReplaySection[];
    responsesBySectionId: Record<string, Record<string, string>>;
};

type TestAnswerReplayDocumentProps = {
    payload: TestAnswerReplayPayload;
    details?: TestAnswerPdfDetails;
    onReady?: () => void;
};

const noopHooks = {};
const REPLAY_LANGUAGE = "eng";

function getSectionResponses(
    responsesBySectionId: TestAnswerReplayPayload["responsesBySectionId"],
    sectionId: number,
) {
    if (!responsesBySectionId) return {};
    return responsesBySectionId[String(sectionId)] ?? {};
}

export default function TestAnswerReplayDocument(props: TestAnswerReplayDocumentProps) {
    const { payload, details, onReady } = props;

    useEffect(() => {
        const timer = window.setTimeout(() => {
            onReady?.();
        }, 500);
        return () => window.clearTimeout(timer);
    }, [onReady, payload]);

    return (
        <div className="App startPage testAnswerReplayRoot">
            {details?.rows?.length ? (
                <div className="testAnswerReplayPage testAnswerPdfTitlePage">
                    <TestAnswerPdfDetailsPage details={details} />
                </div>
            ) : null}
            {payload.sections.map((section) => {
                const pageCount = Math.max(Number(section.pages || 0), 1);
                const sectionResponses = getSectionResponses(payload.responsesBySectionId, section.sectionId);

                return Array.from({ length: pageCount }, (_, pageIndex) => {
                    const pageNumber = pageIndex + 1;
                    const currentSection: SectionCurrentSection = {
                        id: section.sectionId,
                        sectionId: section.sectionId,
                        name: section.name,
                        formId: section.formId,
                        pages: pageCount,
                        order: section.order,
                        contents: section.contents,
                    };

                    return (
                        <div
                            key={`${section.sectionId}-${pageNumber}`}
                            className="testAnswerReplayPage"
                        >
                            <SectionRenderer
                                fixedPage={pageNumber}
                                initialValues={sectionResponses}
                                sectionData={{
                                    currentSection,
                                    lang: REPLAY_LANGUAGE,
                                    testState: {
                                        replayMode: true,
                                        lang: REPLAY_LANGUAGE,
                                    },
                                }}
                                hooks={noopHooks}
                            />
                        </div>
                    );
                });
            })}
        </div>
    );
}
